import { motion, useReducedMotion } from 'framer-motion';
import { Compass, LayoutTemplate, Zap, Rocket, ArrowRight, Clock, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const ease = [0.22, 1, 0.36, 1];

const sectionReveal = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.06,
    },
  },
};

const itemReveal = {
  hidden: { opacity: 0, y: 26 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease },
  },
};

const softRise = {
  hidden: { opacity: 0, y: 18, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.65, ease },
  },
};

const steps = [
  {
    number: '01',
    icon: Compass,
    title: 'Discovery',
    description: 'We start by understanding your business, goals, and the kind of online presence you want to create.',
    details: ['Business overview', 'Goals and priorities', 'Audience expectations'],
  },
  {
    number: '02',
    icon: LayoutTemplate,
    title: 'Planning',
    description: 'We shape the structure, messaging, and visual direction so the website has a strong foundation before development begins.',
    details: ['Site structure', 'Messaging direction', 'Visual references'],
  },
  {
    number: '03',
    icon: Zap,
    title: 'Build',
    description: 'We design and build a modern, responsive website tailored to your business and focused on clarity, trust, and conversion.',
    details: ['Design and development', 'Responsive refinement', 'Content placement'],
    timing: 'Build time: around 1-2 days',
  },
  {
    number: '04',
    icon: Rocket,
    title: 'Launch',
    description: 'After feedback and final refinements, your website goes live ready to support your business professionally online.',
    details: ['Final review', 'Refinements', 'Go-live setup'],
  },
];

const includedFeatures = [
  'Custom website structure',
  'Responsive design',
  'Modern user experience',
  'Contact and CTA sections',
  'Strong visual presentation',
  'SEO-friendly foundations',
];

const heroHighlights = ['Clear process', 'Premium execution', 'Structured delivery'];

function AnimatedGlow({ className, duration, delay, shouldReduceMotion }) {
  return (
    <motion.div
      aria-hidden="true"
      className={className}
      animate={shouldReduceMotion ? undefined : { y: [0, -12, 0], x: [0, 8, 0], scale: [1, 1.04, 1] }}
      transition={
        shouldReduceMotion
          ? undefined
          : { duration, delay, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut' }
      }
    />
  );
}

export default function HowItWorksPage() {
  const shouldReduceMotion = useReducedMotion();
  const hoverLift = shouldReduceMotion
    ? {}
    : { y: -6, scale: 1.01, transition: { duration: 0.25, ease } };

  return (
    <div className="pt-20 overflow-hidden">
      <section className="relative py-20 sm:py-24 lg:py-32">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.05),_transparent_36%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,_rgba(6,182,212,0.08)_0%,_transparent_35%,_transparent_100%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,_rgba(255,255,255,0.02)_1px,_transparent_1px),linear-gradient(180deg,_rgba(255,255,255,0.02)_1px,_transparent_1px)] bg-[size:44px_44px] opacity-30" />
          <AnimatedGlow
            shouldReduceMotion={shouldReduceMotion}
            duration={15}
            delay={0}
            className="absolute -top-16 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-cyan-500/12 blur-[110px] sm:h-80 sm:w-80"
          />
          <AnimatedGlow
            shouldReduceMotion={shouldReduceMotion}
            duration={18}
            delay={1.4}
            className="absolute right-[-5rem] top-24 h-56 w-56 rounded-full bg-violet-500/10 blur-[100px] sm:h-72 sm:w-72"
          />
          <AnimatedGlow
            shouldReduceMotion={shouldReduceMotion}
            duration={16}
            delay={0.8}
            className="absolute bottom-0 left-[-4rem] h-48 w-48 rounded-full bg-cyan-400/10 blur-[90px] sm:h-64 sm:w-64"
          />
        </div>

        <div className="section-container relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={sectionReveal}
            className="mx-auto max-w-4xl text-center"
          >
            <motion.div variants={itemReveal} className="mb-6 inline-flex items-center rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-gray-300 backdrop-blur-sm">
              A simple, structured path from idea to launch
            </motion.div>

            <motion.h1
              variants={itemReveal}
              className="text-5xl font-black leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="how-it-works-headline"
            >
              How It Works
            </motion.h1>

            <motion.p
              variants={itemReveal}
              className="mx-auto mt-6 max-w-3xl text-base leading-relaxed text-gray-300 sm:text-lg lg:text-xl"
            >
              A clear process from first idea to finished website. We make the process simple and structured, so you always know what comes next and your project moves forward with clarity.
            </motion.p>

            <motion.div
              variants={sectionReveal}
              className="mt-10 flex flex-wrap items-center justify-center gap-3 sm:gap-4"
            >
              {heroHighlights.map((item) => (
                <motion.div
                  key={item}
                  variants={softRise}
                  className="rounded-full border border-white/10 bg-[#111111]/80 px-4 py-2 text-sm font-medium text-gray-200 shadow-[0_12px_32px_rgba(0,0,0,0.22)] backdrop-blur-sm"
                >
                  {item}
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="relative py-16 sm:py-20 lg:py-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,_rgba(6,182,212,0.03)_0%,_transparent_28%,_transparent_100%)]" />
          <div className="absolute left-1/2 top-20 hidden h-[32rem] w-px -translate-x-1/2 bg-gradient-to-b from-cyan-500/0 via-cyan-500/20 to-transparent lg:block" />
        </div>

        <div className="section-container relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-15% 0px -10% 0px' }}
            variants={sectionReveal}
            className="mx-auto mb-10 max-w-3xl text-center sm:mb-14 lg:mb-16"
          >
            <motion.p variants={itemReveal} className="mb-3 text-sm font-medium uppercase tracking-[0.22em] text-cyan-400/90">
              Our Process
            </motion.p>
            <motion.h2 variants={itemReveal} className="text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Clear steps, handled properly
            </motion.h2>
            <motion.p variants={itemReveal} className="mt-4 text-base leading-relaxed text-gray-400 sm:text-lg">
              Each stage is designed to keep the project easy to follow, well-paced, and professionally managed.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-10% 0px -10% 0px' }}
            variants={sectionReveal}
            className="space-y-4 lg:hidden"
          >
            {steps.map((step, index) => {
              const Icon = step.icon;

              return (
                <motion.div key={step.number} variants={softRise} className="relative pl-8">
                  {index < steps.length - 1 && (
                    <div className="absolute bottom-[-1.25rem] left-[1.05rem] top-16 w-px bg-gradient-to-b from-cyan-400/40 via-cyan-400/20 to-transparent" />
                  )}

                  <motion.article
                    whileHover={hoverLift}
                    className="relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,_rgba(255,255,255,0.07),_rgba(255,255,255,0.03))] p-5 shadow-[0_18px_40px_rgba(0,0,0,0.28)] backdrop-blur-sm"
                  >
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(6,182,212,0.14),_transparent_38%)] opacity-70" />
                    <div className="relative">
                      <div className="absolute -left-[2.15rem] top-4 flex h-9 w-9 items-center justify-center rounded-full border border-cyan-400/40 bg-[#0f1114] text-xs font-bold text-cyan-300 shadow-[0_0_0_8px_rgba(10,10,10,0.85)]">
                        {step.number}
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-violet-500 shadow-[0_12px_30px_rgba(34,211,238,0.18)]">
                          <Icon size={22} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium uppercase tracking-[0.22em] text-cyan-300/90">Step {step.number}</p>
                          <h3 className="mt-1 text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {step.title}
                          </h3>
                          {step.timing && (
                            <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-xs font-medium text-cyan-300">
                              <Clock size={12} />
                              {step.timing}
                            </div>
                          )}
                        </div>
                      </div>

                      <p className="mt-5 text-sm leading-7 text-gray-300">{step.description}</p>

                      <div className="mt-5 grid grid-cols-1 gap-2">
                        {step.details.map((detail) => (
                          <div key={detail} className="flex items-center gap-3 rounded-2xl border border-white/6 bg-black/10 px-3 py-3 text-sm text-gray-400">
                            <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-cyan-400" />
                            <span>{detail}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.article>
                </motion.div>
              );
            })}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-10% 0px -10% 0px' }}
            variants={sectionReveal}
            className="hidden gap-6 lg:grid lg:grid-cols-2 xl:grid-cols-4"
          >
            {steps.map((step, index) => {
              const Icon = step.icon;

              return (
                <motion.article
                  key={step.number}
                  variants={softRise}
                  whileHover={hoverLift}
                  className="group relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,_rgba(255,255,255,0.065),_rgba(255,255,255,0.025))] p-7 shadow-[0_20px_50px_rgba(0,0,0,0.28)] backdrop-blur-sm"
                >
                  {index < steps.length - 1 && (
                    <div className="pointer-events-none absolute right-[-1.5rem] top-14 hidden h-px w-6 bg-gradient-to-r from-cyan-400/50 to-transparent xl:block" />
                  )}

                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(6,182,212,0.16),_transparent_34%)] opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-80" />

                  <div className="relative flex h-full flex-col">
                    <div className="mb-8 flex items-start justify-between gap-4">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-violet-500 shadow-[0_14px_28px_rgba(34,211,238,0.16)] transition-transform duration-300 group-hover:scale-[1.04] group-hover:-translate-y-1">
                        <Icon size={26} className="text-white" />
                      </div>
                      <span className="text-5xl font-black tracking-tight text-white/6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {step.number}
                      </span>
                    </div>

                    <p className="text-xs font-medium uppercase tracking-[0.22em] text-cyan-300/90">Step {step.number}</p>
                    <h3 className="mt-3 text-[1.9rem] font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {step.title}
                    </h3>
                    <p className="mt-4 text-sm leading-7 text-gray-300">{step.description}</p>

                    <div className="mt-6 space-y-2.5">
                      {step.details.map((detail) => (
                        <div key={detail} className="flex items-center gap-3 text-sm text-gray-400">
                          <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-cyan-400/90" />
                          <span>{detail}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-auto pt-6">
                      {step.timing ? (
                        <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1.5 text-xs font-medium text-cyan-300">
                          <Clock size={12} />
                          {step.timing}
                        </div>
                      ) : (
                        <div className="inline-flex rounded-full border border-white/10 px-3 py-1.5 text-xs text-gray-500">
                          Structured handoff and review
                        </div>
                      )}
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="relative bg-[#0d0d0d] py-16 sm:py-20 lg:py-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute right-0 top-8 h-56 w-56 rounded-full bg-cyan-500/8 blur-[110px] sm:h-72 sm:w-72" />
          <div className="absolute left-0 bottom-0 h-40 w-40 rounded-full bg-violet-500/8 blur-[90px]" />
        </div>

        <div className="section-container relative z-10">
          <div className="grid items-start gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-12% 0px -10% 0px' }}
              variants={sectionReveal}
              className="max-w-xl"
            >
              <motion.p variants={itemReveal} className="mb-3 text-sm font-medium uppercase tracking-[0.22em] text-cyan-400/90">
                Included
              </motion.p>
              <motion.h2 variants={itemReveal} className="text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Everything needed for a strong, professional site
              </motion.h2>
              <motion.p variants={itemReveal} className="mt-5 text-base leading-relaxed text-gray-400 sm:text-lg">
                The foundation is built around presentation, trust, and usability, so the final website feels polished and ready for real business use.
              </motion.p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-12% 0px -10% 0px' }}
              variants={sectionReveal}
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4"
            >
              {includedFeatures.map((feature) => (
                <motion.div
                  key={feature}
                  variants={softRise}
                  whileHover={hoverLift}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.045] p-4 shadow-[0_14px_34px_rgba(0,0,0,0.22)] backdrop-blur-sm sm:p-5"
                >
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(6,182,212,0.12),_transparent_40%)] opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="relative flex items-start gap-3">
                    <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-violet-500 shadow-[0_8px_20px_rgba(34,211,238,0.18)]">
                      <Check size={16} className="text-white" />
                    </div>
                    <span className="text-sm leading-6 text-gray-200 sm:text-base">{feature}</span>
                  </div>
                  <div className="relative mt-4 h-px bg-gradient-to-r from-white/10 via-white/5 to-transparent" />
                  <div className="relative mt-3 text-xs uppercase tracking-[0.18em] text-gray-500">
                    Included by default
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="relative py-16 sm:py-20 lg:py-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/2 top-12 h-56 w-56 -translate-x-1/2 rounded-full bg-violet-500/10 blur-[110px]" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        <div className="section-container relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-12% 0px -10% 0px' }}
            variants={sectionReveal}
            className="mx-auto max-w-4xl overflow-hidden rounded-[2rem] border border-white/10 bg-[linear-gradient(180deg,_rgba(255,255,255,0.06),_rgba(255,255,255,0.025))] px-6 py-10 text-center shadow-[0_24px_70px_rgba(0,0,0,0.34)] backdrop-blur-sm sm:px-8 sm:py-12 lg:px-12 lg:py-14"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(6,182,212,0.14),_transparent_34%)]" />

            <motion.h2
              variants={itemReveal}
              className="relative text-4xl font-black leading-[1.05] sm:text-5xl lg:text-6xl"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Ready to move forward?
            </motion.h2>

            <motion.p
              variants={itemReveal}
              className="relative mx-auto mt-5 max-w-2xl text-base leading-relaxed text-gray-300 sm:text-lg lg:text-xl"
            >
              Let&apos;s discuss your business and build a website that fits it properly.
            </motion.p>

            <motion.div
              variants={sectionReveal}
              className="relative mt-8 flex flex-col justify-center gap-4 sm:flex-row"
            >
              <motion.div variants={softRise}>
                <Link to="/contact" className="btn-primary group flex items-center justify-center gap-2">
                  Start Your Project
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </motion.div>

              <motion.div variants={softRise}>
                <Link to="/pricing" className="btn-secondary">
                  View Pricing
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}